import { createElement } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { buildCollection, type Entity, type EntityAction, type EntityActionClickProps, type EntityCollection } from "@firecms/core";
import { DeleteForeverIcon, DeleteOutlineIcon, ReceiptLongIcon } from "@firecms/ui";
import { functionsRegion } from "../firebase_config";
import { rolesOf } from "../roles";
import { PhonePreview } from "./phone";

const STATUS = {
  pending_payment: { id: "pending_payment", label: "等待付款", color: "grayLight" },
  cod_pending: { id: "cod_pending", label: "貨到付款・待出貨", color: "yellowLight" },
  paid: { id: "paid", label: "已付款", color: "blueLight" },
  shipped: { id: "shipped", label: "已出貨", color: "greenLight" },
  completed: { id: "completed", label: "已完成", color: "greenDark" },
  expired: { id: "expired", label: "已逾期", color: "grayDark" },
  refunded: { id: "refunded", label: "已退款", color: "orangeLight" },
  cancelled: { id: "cancelled", label: "已取消", color: "redLight" },
} as const;

const amountNote = "金額單位是該幣別的最小單位：新台幣、日圓為元；美元、港幣、歐元、新加坡幣為分（例如 32900 = 329.00）。";

/** 移除與永久刪除都呼叫 dataSource.deleteEntity，由 App.tsx 的委派依畫面決定軟移除或實體刪除。 */
async function deleteOrder(
  { entity, collection, context, onCollectionChange, selectionController }: EntityActionClickProps<Record<string, unknown>>,
  success: string,
) {
  if (!entity) return;
  try {
    await context.dataSource.deleteEntity({ entity, collection });
    selectionController?.setSelectedEntities(selectionController.selectedEntities.filter((selected) => selected.id !== entity.id || selected.path !== entity.path));
    onCollectionChange?.();
    context.snackbarController.open({ type: "success", message: success });
  } catch (error) {
    context.snackbarController.open({ type: "error", message: error instanceof Error ? error.message : String(error) });
  }
}

const removeOrderAction: EntityAction = {
  key: "delete",
  name: "移除訂單",
  icon: createElement(DeleteOutlineIcon, { size: "small" }),
  includeInForm: false,
  isEnabled: ({ entity, context }) => !!entity && entity.values.removed !== true && rolesOf(context.authController).staff,
  async onClick(props) {
    const { entity, context } = props;
    if (!entity || entity.values.removed === true || !rolesOf(context.authController).staff) return;
    if (!window.confirm(`確定要移除訂單 ${entity.id} 嗎？移除後只會出現在「已移除訂單」。這不會取消訂單、退款或回補庫存。`)) return;
    await deleteOrder(props, `訂單 ${entity.id} 已移至「已移除訂單」`);
  },
};

/**
 * 不能永久刪除的原因；可以刪時回 undefined。
 * 等待付款的訂單還保留庫存、要等逾期排程釋放，刪掉就不會回補，所以要等逾期後才能刪。Firestore 規則也有同樣的限制。
 */
export function orderPurgeBlocker(entity: Entity<Record<string, unknown>>): string | undefined {
  if (entity.values.removed !== true) return `訂單 ${entity.id} 尚未移除，不能永久刪除。`;
  if (entity.values.status === "pending_payment") return `訂單 ${entity.id} 還在等待付款，逾期後才能永久刪除。`;
  return undefined;
}

const purgeOrderAction: EntityAction = {
  key: "delete",
  name: "永久刪除",
  icon: createElement(DeleteForeverIcon, { size: "small" }),
  includeInForm: false,
  isEnabled: ({ entity, context }) => !!entity && !orderPurgeBlocker(entity) && rolesOf(context.authController).admin,
  async onClick(props) {
    const { entity, context } = props;
    if (!entity || orderPurgeBlocker(entity) || !rolesOf(context.authController).admin) return;
    if (!window.confirm(`確定要永久刪除訂單 ${entity.id} 嗎？刪除後無法復原，後台與顧客查單都找不到這筆訂單。這不會退款、回補庫存或作廢發票。`)) return;
    await deleteOrder(props, `訂單 ${entity.id} 已永久刪除`);
  },
};

/**
 * 訂單。金額、品項、付款資料由伺服器寫入，後台唯讀；
 * 可以改的是狀態、追蹤碼與內部備註。已付款或貨到付款待出貨的訂單填入追蹤碼後，系統會自動改成「已出貨」並寄出貨通知；
 * 更正追蹤碼會再寄一次出貨通知。
 * 貨到付款訂單在黑貓撥款後再改成「已完成」。
 * 發票不會自動開，也和訂單狀態脫鉤：寄台灣、用綠界或貨到付款的訂單出現在「待開發票」，由人員決定什麼時候按「開立發票」。
 */
function ordersCollection(overrides: Partial<EntityCollection>): EntityCollection {
  return buildCollection({
    id: "orders",
    path: "orders",
    name: "全部訂單",
    singularName: "訂單",
    group: "營運",
    icon: "receipt_long",
    description: amountNote,
    permissions: ({ authController, entity }) => {
      const r = rolesOf(authController);
      const active = entity?.values.removed !== true;
      return { read: r.staff, edit: r.staff && active, create: false, delete: r.staff && active };
    },
    initialSort: ["createdAt", "desc"],
    textSearchEnabled: false,
    entityActions: [removeOrderAction],
    propertiesOrder: ["status", "createdAt", "email", "recipient", "currency", "amounts", "discountCode", "items", "shipment", "invoice", "payment", "staffNotes", "notes", "flags", "locale", "expiresAt"],
    properties: {
      status: { name: "狀態", dataType: "string", enumValues: STATUS, validation: { required: true } },
      createdAt: { name: "下單時間", dataType: "date", readOnly: true },
      removed: { name: "已移除", dataType: "boolean", readOnly: true, hideFromCollection: true },
      email: { name: "Email", dataType: "string", readOnly: true },
      locale: { name: "語系", dataType: "string", readOnly: true },
      currency: { name: "幣別", dataType: "string", readOnly: true },
      amounts: {
        name: "金額",
        description: amountNote,
        dataType: "map",
        readOnly: true,
        properties: {
          subtotal: { name: "小計", dataType: "number" },
          shipping: { name: "運費", dataType: "number" },
          discount: { name: "折扣", dataType: "number" },
          total: { name: "合計", dataType: "number" },
        },
      },
      discountCode: { name: "折扣碼", description: "折抵的金額在「金額」的折扣", dataType: "string", readOnly: true },
      items: {
        name: "品項",
        dataType: "array",
        readOnly: true,
        of: {
          dataType: "map",
          properties: {
            sku: { name: "SKU", dataType: "string" },
            nameZh: { name: "品名", dataType: "string" },
            qty: { name: "數量", dataType: "number" },
            lineTotal: { name: "金額", dataType: "number" },
          },
        },
      },
      // PCCC 只有寄韓國才有，其他訂單不顯示這一列
      recipient: ({ values }) => ({
        name: "收件人",
        dataType: "map",
        readOnly: true,
        properties: {
          name: { name: "姓名", dataType: "string" },
          phone: { name: "電話", dataType: "string", Preview: PhonePreview },
          ...(values.recipient?.address?.country === "KR" ? { pccc: { name: "PCCC（韓國）", dataType: "string" } } : {}),
          address: {
            name: "地址",
            dataType: "map",
            properties: {
              country: { name: "國家", dataType: "string" },
              postalCode: { name: "郵遞區號", dataType: "string" },
              region: { name: "州／省", dataType: "string" },
              city: { name: "縣市／城市", dataType: "string" },
              district: { name: "鄉鎮市區", dataType: "string" },
              line1: { name: "地址 1", dataType: "string" },
              line2: { name: "地址 2", dataType: "string" },
            },
          },
        },
      }),
      shipment: {
        name: "出貨",
        description: "在黑貓、順豐或 EMS 後台建好託運單後，把追蹤碼填在這裡；系統會自動把狀態改成「已出貨」並寄出貨通知。更正追蹤碼會重寄通知。",
        dataType: "map",
        properties: {
          carrier: { name: "承運商", dataType: "string", enumValues: { tcat: "黑貓", sf: "順豐", ems: "EMS" } },
          trackingNo: { name: "追蹤碼", dataType: "string" },
          shippedAt: { name: "出貨時間", dataType: "date", readOnly: true },
          notifiedAt: { name: "已寄出貨通知", dataType: "date", readOnly: true },
          zoneId: { name: "運費區域", dataType: "string", readOnly: true },
        },
      },
      invoice: {
        name: "電子發票",
        dataType: "map",
        readOnly: true,
        properties: {
          status: {
            name: "發票狀態",
            dataType: "string",
            enumValues: { pending: "待開立", issuing: "開立中", issued: "已開立", needs_manual: "需人工處理", failed: "開立失敗", not_required: "不需開立" },
          },
          number: { name: "發票號碼", dataType: "string" },
          date: { name: "開立時間", dataType: "string" },
          error: { name: "錯誤訊息", dataType: "string" },
        },
      },
      payment: {
        name: "付款",
        dataType: "map",
        readOnly: true,
        properties: {
          provider: { name: "金流", dataType: "string", enumValues: { ecpay: "綠界", paypal: "PayPal", cod: "貨到付款" } },
          paymentType: { name: "付款方式", dataType: "string" },
          tradeNo: { name: "綠界交易編號", dataType: "string" },
          paypalOrderId: { name: "PayPal 訂單", dataType: "string" },
          captureId: { name: "PayPal 交易", dataType: "string" },
          paidAt: { name: "付款時間", dataType: "date" },
          simulated: { name: "模擬付款", dataType: "boolean" },
        },
      },
      staffNotes: { name: "內部備註", dataType: "string", multiline: true },
      notes: { name: "顧客備註", dataType: "string", multiline: true, readOnly: true },
      flags: {
        name: "需要注意",
        dataType: "map",
        readOnly: true,
        properties: {
          amountMismatch: { name: "金額不符", dataType: "string" },
          paidAfterExpiry: { name: "逾期後才付款", dataType: "boolean" },
          paypalRefund: { name: "PayPal 退款／爭議", dataType: "string" },
        },
      },
      expiresAt: { name: "付款期限", dataType: "date", readOnly: true, description: "貨到付款訂單沒有付款期限" },
    },
    ...overrides,
    forceFilter: { removed: ["==", false], ...overrides.forceFilter },
  } as EntityCollection);
}

/**
 * 開立發票：呼叫 Cloud Function issueOrderInvoice，透過綠界開立。
 * 不看訂單狀態，發票待開立或開立失敗就能按（伺服器端也會檢查）；什麼時候開由人員決定。
 */
const issueInvoiceAction: EntityAction = {
  key: "issue_invoice",
  name: "開立發票",
  icon: createElement(ReceiptLongIcon, { size: "small" }),
  isEnabled: ({ entity, context }) => entity?.values.removed !== true && rolesOf(context.authController).staff && ["pending", "failed"].includes(entity?.values.invoice?.status),
  async onClick({ entity, context }) {
    if (!entity || entity.values.removed === true || !rolesOf(context.authController).staff) return;
    if (!window.confirm(`確定要透過綠界開立訂單 ${entity.id} 的電子發票嗎？`)) return;
    const issue = httpsCallable<{ orderId: string }, { ok: true; number: string; date: string }>(
      getFunctions(getApp(), functionsRegion),
      "issueOrderInvoice",
    );
    try {
      const { data } = await issue({ orderId: entity.id });
      context.snackbarController.open({ type: "success", message: `已開立發票 ${data.number}（訂單 ${entity.id}）` });
    } catch (error) {
      context.snackbarController.open({ type: "error", message: error instanceof Error ? error.message : String(error) });
    }
  },
};

export const allOrdersCollection = ordersCollection({ entityActions: [issueInvoiceAction, removeOrderAction] });

/** 待出貨：已付款，以及貨到付款待出貨的訂單 */
export const toShipCollection = ordersCollection({
  id: "orders_to_ship",
  name: "待出貨",
  icon: "local_shipping",
  forceFilter: { status: ["in", ["paid", "cod_pending"]] },
  initialSort: ["createdAt", "asc"],
});

/**
 * 待開發票：發票待開立或開立失敗的訂單，不看出貨天數，什麼時候開由人員決定。
 * 不列還沒成立或已經不成立的訂單（等待付款、逾期、取消、退款）；這只是清單的篩選，開立本身不看訂單狀態。
 */
export const toInvoiceCollection = ordersCollection({
  id: "orders_to_invoice",
  name: "待開發票",
  icon: "request_quote",
  description: `發票待開立或開立失敗的訂單；只有寄台灣、用綠界或貨到付款的訂單要開，不列等待付款、逾期、取消、退款的訂單。什麼時候開由人員決定（通常出貨後 5～10 天，避開鑑賞期內的退貨），系統不檢查出貨天數，請看「出貨」欄的出貨時間。在訂單的選單按「開立發票」透過綠界開立，結果記在「電子發票」欄。直接在綠界發票系統開的發票不會回到後台，那筆訂單會留在清單上，不要再按「開立發票」，否則會重複開立。作廢、折讓、補開請在綠界發票系統處理，一切以綠界為準。${amountNote}`,
  forceFilter: {
    status: ["in", ["paid", "cod_pending", "shipped", "completed"]],
    "invoice.status": ["in", ["pending", "failed"]],
  },
  initialSort: ["createdAt", "asc"],
  entityActions: [issueInvoiceAction, removeOrderAction],
});

export const removedOrdersCollection = ordersCollection({
  id: "orders_removed",
  name: "已移除訂單",
  icon: "delete_outline",
  description: `這些訂單已從一般清單移除，不能編輯或開立發票。移除不會取消訂單、退款或回補庫存；顧客查單、付款通知與逾期處理不受影響。管理員可以勾選後按右上方垃圾桶，或在訂單的選單按「永久刪除」；永久刪除無法復原，刪除後顧客也查不到這筆訂單。等待付款的訂單要等逾期後才能刪除。${amountNote}`,
  forceFilter: { removed: ["==", true] },
  permissions: ({ authController, entity }) => {
    const r = rolesOf(authController);
    return { read: r.staff, edit: false, create: false, delete: r.admin && (!entity || !orderPurgeBlocker(entity)) };
  },
  entityActions: [purgeOrderAction],
});
